/**
 * QdsDiagramManager Web Component
 *
 * A full-screen diagram management interface with integrated file explorer and diagram viewer/editor.
 * Combines qds-tree-view and qds-diagram-host components in a split-panel layout.
 *
 * @element qds-diagram-manager
 *
 * @attr {String} storage-account - Azure storage account name
 * @attr {String} container - Azure storage container name
 * @attr {String} base-path - Base path to filter files (optional)
 * @attr {String} initial-path - Initial file path to navigate to and open on load (optional)
 * @attr {String} tree-width - Initial width of tree panel (default: 300px)
 * @attr {String} auth-method - Authentication method: 'quantum-auth' | 'sas-token' (default: quantum-auth)
 * @attr {String} sas-token - SAS token (required if auth-method is 'sas-token')
 *
 * @csspart container - The main container
 * @csspart tree-panel - The tree view panel
 * @csspart diagram-panel - The diagram host panel
 * @csspart resize-handle - The resize handle between panels
 *
 * @fires file-selected - When a file is selected in the tree { detail: { node: TreeNode } }
 * @fires diagram-loaded - When a diagram is loaded { detail: { path: string, name: string } }
 * @fires error - When an error occurs { detail: { error: Error } }
 */

import { AzureStorageTreeProvider } from '../providers/AzureStorageTreeProvider.js';
import type { ITreeDataProvider } from '../providers/ITreeDataProvider.js';
import type { TreeNode } from '../../types/index.js';
import { loadBaseCSS } from '../shared/button-utils.js';

loadBaseCSS();

interface FileSelectedEventDetail {
  node: TreeNode;
}

interface DiagramLoadedEventDetail {
  path: string;
  name: string;
}

interface ErrorEventDetail {
  error: Error;
}

class QdsDiagramManager extends HTMLElement {
  private _provider: ITreeDataProvider | null;
  private _treeView: HTMLElement | null;
  private _diagramHost: HTMLElement | null;
  private _treeWidth: string;
  private _selectedNode: TreeNode | null;
  private _initialPathHandled: boolean;
  private _contextMenu: HTMLElement | null;
  private _contextMenuNode: TreeNode | null;
  private _clipboardNode: TreeNode | null;
  private _clipboardOperation: 'cut' | 'copy' | null;
  private _resizeHandle: HTMLElement | null;
  private readonly _handleAuthInitialized: EventListener;
  private readonly _handleFileSelectedEvent: EventListener;
  private readonly _handleNodeSelectedEvent: EventListener;
  private readonly _handleContextMenuEvent: EventListener;
  private readonly _handleDocumentClickEvent: EventListener;
  private readonly _handleResizeStartEvent: EventListener;

  constructor() {
    super();

    this._provider = null;
    this._treeView = null;
    this._diagramHost = null;
    this._treeWidth = '300px';
    this._selectedNode = null;
    this._initialPathHandled = false;
    this._contextMenu = null;
    this._contextMenuNode = null;
    this._clipboardNode = null;
    this._clipboardOperation = null;
    this._resizeHandle = null;
    this._handleAuthInitialized = () => {
      console.log('[qds-diagram-manager] quantum-auth initialized, now initializing provider');
      this._initializeProvider();
      document.removeEventListener('quantum-auth-initialized', this._handleAuthInitialized);
    };
    this._handleFileSelectedEvent = this._handleFileSelected.bind(this) as EventListener;
    this._handleNodeSelectedEvent = this._handleNodeSelected.bind(this) as EventListener;
    this._handleContextMenuEvent = this._handleContextMenu.bind(this) as EventListener;
    this._handleDocumentClickEvent = this._handleDocumentClick.bind(this) as EventListener;
    this._handleResizeStartEvent = this._handleResizeStart.bind(this) as EventListener;

    // Create shadow DOM
    this.attachShadow({ mode: 'open' });

    // Initial render
    this._render();
  }

  static get observedAttributes(): string[] {
    return ['storage-account', 'container', 'base-path', 'initial-path', 'tree-width', 'auth-method', 'sas-token'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'tree-width':
        this._treeWidth = newValue || '300px';
        this._updateTreeWidth();
        break;
      case 'storage-account':
      case 'container':
      case 'auth-method':
      case 'sas-token':
      case 'base-path':
        // Angular and other framework wrappers apply attributes after the
        // custom element connects. Reinitialize when the values arrive, even
        // when no provider has been created yet.
        if (this.isConnected && (window as any).getQuantumAuth) {
          console.log('[qds-diagram-manager] Attribute changed, initializing provider');
          this._initializeProvider();
        }
        break;
    }
  }

  connectedCallback(): void {
    if (!this.isConnected) return;

    // Wait for quantum-auth to be ready before initializing provider
    if ((window as any).getQuantumAuth) {
      // Auth already initialized
      this._initializeProvider();
    } else {
      // Wait for auth initialization
      console.log('[qds-diagram-manager] Waiting for quantum-auth to initialize...');
      document.addEventListener('quantum-auth-initialized', this._handleAuthInitialized);
    }
  }

  disconnectedCallback(): void {
    document.removeEventListener('quantum-auth-initialized', this._handleAuthInitialized);
    document.removeEventListener('click', this._handleDocumentClickEvent);

    if (this._treeView) {
      this._treeView.removeEventListener('file-selected', this._handleFileSelectedEvent);
      this._treeView.removeEventListener('node-selected', this._handleNodeSelectedEvent);
      this._treeView.removeEventListener('node-contextmenu', this._handleContextMenuEvent);
    }

    if (this._resizeHandle) {
      this._resizeHandle.removeEventListener('mousedown', this._handleResizeStartEvent);
    }
  }

  /**
   * Get the data provider
   */
  get dataProvider(): ITreeDataProvider | null {
    return this._provider;
  }

  /**
   * Set the data provider
   */
  set dataProvider(provider: ITreeDataProvider) {
    this._provider = provider;

    if (this._treeView) {
      (this._treeView as any).dataProvider = provider;
    }
  }

  /**
   * Get the currently selected node
   */
  get selectedNode(): TreeNode | null {
    return this._selectedNode;
  }

  /**
   * Initialize the data provider based on attributes
   */
  private _initializeProvider(): void {
    const storageAccount = this.getAttribute('storage-account');
    const container = this.getAttribute('container');

    console.log('[qds-diagram-manager] Initializing provider with:', { storageAccount, container });

    // A framework wrapper can connect the element before it assigns inputs.
    // In that case, wait for attributeChangedCallback rather than reporting a
    // configuration error for an element that is still being initialized.
    if (!storageAccount || !container) return;

    const authMethod = this.getAttribute('auth-method') || 'quantum-auth';
    const sasToken = this.getAttribute('sas-token');
    const basePath = this.getAttribute('base-path') || undefined;

    try {
      this._provider = new AzureStorageTreeProvider({
        storageAccount,
        container,
        basePath,
        authMethod: authMethod as 'quantum-auth' | 'sas-token',
        sasToken: sasToken || undefined,
        allowedExtensions: ['.drawio', '.xml', '.svg'] // Filter to diagram files
      });

      console.log('[qds-diagram-manager] Provider created:', this._provider);

      // Set provider on tree-view
      if (this._treeView) {
        console.log('[qds-diagram-manager] Setting provider on tree-view');
        (this._treeView as any).dataProvider = this._provider;

        // After provider is set, navigate to initial path if specified
        this._navigateToInitialPath();
      } else {
        console.warn('[qds-diagram-manager] Tree-view not found, cannot set provider');
      }
    } catch (error) {
      console.error('[qds-diagram-manager] Error initializing provider:', error);
      this._emitError(error as Error);
    }
  }

  /**
   * Navigate to the initial path if specified and not already handled
   */
  private async _navigateToInitialPath(): Promise<void> {
    // Only navigate once
    if (this._initialPathHandled) {
      return;
    }

    // Get initial path from attribute, or fall back to URL query string
    let initialPath = this.getAttribute('initial-path');

    // If no explicit initial-path attribute, check URL query string for 'qds-diagram-manager-path' parameter
    if (!initialPath) {
      const urlParams = new URLSearchParams(window.location.search);
      initialPath = urlParams.get('qds-diagram-manager-path');
      if (initialPath) {
        console.log('[qds-diagram-manager] Found initial path in URL query string:', initialPath);
      }
    }

    if (!initialPath || !this._provider) {
      return;
    }

    console.log('[qds-diagram-manager] Navigating to initial path:', initialPath);
    this._initialPathHandled = true;

    try {
      // Wait a bit for the tree to load
      await new Promise(resolve => setTimeout(resolve, 500));

      // Try to get the node at the initial path
      const node = await this._provider.getNode(initialPath);

      if (!node) {
        console.warn('[qds-diagram-manager] Initial path node not found:', initialPath);
        return;
      }

      if (node.type !== 'file') {
        console.warn('[qds-diagram-manager] Initial path is not a file:', initialPath);
        return;
      }

      // Check if it's a diagram file
      if (this._isDrawioFile(node.name)) {
        console.log('[qds-diagram-manager] Loading initial diagram:', node.name);
        this._loadDiagram(node);
      } else {
        console.warn('[qds-diagram-manager] Initial path is not a diagram file:', node.name);
      }

    } catch (error) {
      console.error('[qds-diagram-manager] Error navigating to initial path:', error);
      this._emitError(error as Error);
    }
  }

  /**
   * Handle file selection from tree-view
   */
  private _handleFileSelected(event: Event): void {
    const customEvent = event as CustomEvent<FileSelectedEventDetail>;
    const { node } = customEvent.detail;

    console.log('[qds-diagram-manager] File selected:', {
      name: node.name,
      type: node.type,
      path: node.path,
      isDrawio: this._isDrawioFile(node.name)
    });

    this._selectedNode = node;

    // Only load diagram files
    if (node.type === 'file' && this._isDrawioFile(node.name)) {
      console.log('[qds-diagram-manager] File is a diagram, loading...');
      this._loadDiagram(node);
    } else {
      console.log('[qds-diagram-manager] File is not a diagram or is a folder, skipping load. Type:', node.type, 'isDrawio:', this._isDrawioFile(node.name));
    }

    // Emit file-selected event
    this.dispatchEvent(new CustomEvent<FileSelectedEventDetail>('file-selected', {
      detail: { node },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Handle node selection (folders or files)
   */
  private _handleNodeSelected(event: Event): void {
    const customEvent = event as CustomEvent<FileSelectedEventDetail>;
    const { node } = customEvent.detail;

    this._selectedNode = node;
  }

  /**
   * Check if file is a diagram file
   */
  private _isDrawioFile(filename: string): boolean {
    const ext = filename.split('.').pop()?.toLowerCase();
    console.log('[qds-diagram-manager] Checking extension for:', filename, 'extracted:', ext);
    return ext === 'drawio' || ext === 'xml' || ext === 'svg';
  }

  /**
   * Load diagram into diagram-host
   */
  private _loadDiagram(node: TreeNode): void {
    if (!this._diagramHost) return;

    try {
      // Extract directory path and filename
      const pathParts = node.path.split('/');
      const fileName = pathParts.pop() || '';
      const directoryPath = pathParts.join('/'); // Relative path within container

      // Get storage account and container
      const storageAccount = this.getAttribute('storage-account');
      const container = this.getAttribute('container');

      console.log('[qds-diagram-manager] Loading diagram:', {
        fileName,
        directoryPath,
        storageAccount,
        container
      });

      // Set diagram-host attributes (path is relative to container)
      this._diagramHost.setAttribute('storage-account', storageAccount || '');
      this._diagramHost.setAttribute('container', container || '');
      this._diagramHost.setAttribute('path', directoryPath); // Empty string if at root
      this._diagramHost.setAttribute('drawing-name', fileName);
      this._diagramHost.setAttribute('width', '100%');
      this._diagramHost.setAttribute('height', '100%');

      // Show diagram-host, hide empty state
      const emptyState = this.shadowRoot?.querySelector('#empty-state') as HTMLElement;
      if (emptyState) {
        emptyState.style.display = 'none';
      }
      (this._diagramHost as HTMLElement).style.display = 'block';

      console.log('[qds-diagram-manager] Diagram host updated and visible');

      // Emit diagram-loaded event
      this.dispatchEvent(new CustomEvent<DiagramLoadedEventDetail>('diagram-loaded', {
        detail: { path: directoryPath, name: fileName },
        bubbles: true,
        composed: true
      }));
    } catch (error) {
      console.error('[qds-diagram-manager] Error loading diagram:', error);
      this._emitError(error as Error);
    }
  }

  /**
   * Emit error event
   */
  private _emitError(error: Error): void {
    this.dispatchEvent(new CustomEvent<ErrorEventDetail>('error', {
      detail: { error },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Update tree width
   */
  private _updateTreeWidth(): void {
    const treePanel = this.shadowRoot?.querySelector('.tree-panel') as HTMLElement;
    if (treePanel) {
      treePanel.style.width = this._treeWidth;
    }
  }

  /**
   * Render the component
   */
  private _render(): void {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          --_diagram-manager-surface: var(--theme-background-elevated, var(--theme-background-primary));
          --_diagram-manager-surface-subtle: var(--theme-background-secondary);
          --_diagram-manager-border: var(--theme-border-color);
          --_diagram-manager-hover: var(--theme-background-hover);
          --_diagram-manager-text: var(--theme-text-primary);
          --_diagram-manager-subtle-text: var(--theme-text-secondary);
          --_diagram-manager-shadow: var(--theme-shadow-sm);
          --_diagram-manager-radius-sm: var(--theme-radius-sm, 4px);
          --_diagram-manager-resize-active: var(--theme-accent-primary);
          display: block;
          width: 100%;
          height: 95vh;
          font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
          font-size: var(--theme-font-size-sm, 14px);
          position: relative;
          overflow: hidden;
        }

        *, *::before, *::after {
          box-sizing: border-box;
        }

        .container {
          display: flex;
          width: 100%;
          height: 100%;
          background: var(--_diagram-manager-surface);
        }

        .tree-panel {
          width: ${this._treeWidth};
          min-width: 200px;
          max-width: 600px;
          height: 100%;
          display: flex;
          flex-direction: column;
          border-right: 1px solid var(--_diagram-manager-border);
          position: relative;
          background: var(--_diagram-manager-surface);
        }

        .tree-header {
          padding: var(--theme-space-3, 12px) var(--theme-space-4, 16px);
          border-bottom: 1px solid var(--_diagram-manager-border);
          background: var(--_diagram-manager-surface-subtle);
          font-weight: 500;
          color: var(--_diagram-manager-text);
        }

        .tree-content {
          flex: 1;
          overflow: hidden;
        }

        qds-tree-view {
          width: 100%;
          height: 100%;
        }

        .resize-handle {
          position: absolute;
          top: 0;
          right: 0;
          width: 4px;
          height: 100%;
          background: transparent;
          cursor: col-resize;
          z-index: 10;
        }

        .resize-handle:hover {
          background: var(--_diagram-manager-resize-active);
        }

        .diagram-panel {
          flex: 1;
          height: 100%;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          background: var(--_diagram-manager-surface);
        }

        qds-diagram-host {
          display: block;
          width: 100%;
          height: 100%;
          flex: 1;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: var(--_diagram-manager-subtle-text);
          text-align: center;
          padding: 40px;
        }

        .empty-state-icon {
          font-size: 64px;
          margin-bottom: 16px;
          opacity: 0.3;
        }

        .empty-state-title {
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 8px;
          color: var(--_diagram-manager-text);
        }

        .empty-state-message {
          font-size: var(--theme-font-size-sm, 14px);
          color: var(--_diagram-manager-subtle-text);
        }

        /* Context Menu Styles */
        .context-menu {
          position: fixed;
          background: var(--_diagram-manager-surface);
          border: 1px solid var(--_diagram-manager-border);
          border-radius: var(--_diagram-manager-radius-sm);
          box-shadow: var(--_diagram-manager-shadow);
          padding: 4px 0;
          min-width: 180px;
          z-index: 10000;
          display: none;
        }

        .context-menu.visible {
          display: block;
        }

        .context-menu-item {
          padding: 8px 16px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--_diagram-manager-text);
          transition: background-color 0.15s;
        }

        .context-menu-item:hover:not(.disabled) {
          background: var(--_diagram-manager-hover);
        }

        .context-menu-item:focus-visible {
          outline: 2px solid var(--_diagram-manager-resize-active);
          outline-offset: -2px;
        }

        .context-menu-item.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .context-menu-separator {
          height: 1px;
          background: var(--_diagram-manager-border);
          margin: 4px 0;
        }

        .context-menu-item-icon {
          width: 16px;
          text-align: center;
        }

        .context-menu-item-label {
          flex: 1;
        }

        .context-menu-item-arrow {
          margin-left: auto;
          font-size: 10px;
        }

        .context-submenu {
          position: absolute;
          left: 100%;
          top: 0;
          background: var(--_diagram-manager-surface);
          border: 1px solid var(--_diagram-manager-border);
          border-radius: var(--_diagram-manager-radius-sm);
          box-shadow: var(--_diagram-manager-shadow);
          padding: 4px 0;
          min-width: 160px;
          display: none;
          z-index: 10001;
        }

        .context-menu-item:hover > .context-submenu {
          display: block;
        }

        .context-submenu-item {
          padding: 8px 16px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--_diagram-manager-text);
          transition: background-color 0.15s;
        }

        .context-submenu-item:hover {
          background: var(--_diagram-manager-hover);
        }

        .context-submenu-item-icon {
          width: 16px;
          text-align: center;
        }
      </style>

      <div class="container" part="container">
        <!-- Tree Panel -->
        <div class="tree-panel" part="tree-panel">
          <div class="tree-header">File Explorer</div>
          <div class="tree-content">
            <qds-tree-view id="tree-view" resizable="false"></qds-tree-view>
          </div>
          <div class="resize-handle" part="resize-handle"></div>
        </div>

        <!-- Diagram Panel -->
        <div class="diagram-panel" part="diagram-panel">
          <div class="empty-state" id="empty-state">
            <div class="empty-state-icon">📊</div>
            <div class="empty-state-title">No Diagram Selected</div>
            <div class="empty-state-message">
              Select a diagram file from the file explorer to view and edit it
            </div>
          </div>
          <qds-diagram-host id="diagram-host" style="display: none;"></qds-diagram-host>
        </div>
      </div>

      <!-- Context Menu -->
      <div class="context-menu" id="context-menu" part="context-menu"></div>
    `;

    // Get references
    this._treeView = this.shadowRoot.querySelector('#tree-view');
    this._diagramHost = this.shadowRoot.querySelector('#diagram-host');

    // Set provider on tree-view if available
    if (this._provider && this._treeView) {
      (this._treeView as any).dataProvider = this._provider;
    }

    // Attach event listeners
    if (this._treeView) {
      this._treeView.addEventListener('file-selected', this._handleFileSelectedEvent);
      this._treeView.addEventListener('node-selected', this._handleNodeSelectedEvent);
      this._treeView.addEventListener('node-contextmenu', this._handleContextMenuEvent);
    }

    // Get context menu reference
    this._contextMenu = this.shadowRoot.querySelector('#context-menu');

    // Click outside to close context menu
    document.addEventListener('click', this._handleDocumentClickEvent);

    // Attach resize handle
    this._resizeHandle = this.shadowRoot.querySelector('.resize-handle') as HTMLElement;
    if (this._resizeHandle) {
      this._resizeHandle.addEventListener('mousedown', this._handleResizeStartEvent);
    }
  }

  /**
   * Handle resize start
   */
  private _handleResizeStart(event: MouseEvent): void {
    event.preventDefault();

    const startX = event.clientX;
    const treePanel = this.shadowRoot?.querySelector('.tree-panel') as HTMLElement;
    const startWidth = treePanel.offsetWidth;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(200, Math.min(600, startWidth + deltaX));
      this._treeWidth = `${newWidth}px`;
      this._updateTreeWidth();
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }

  /**
   * Handle context menu event from tree-view
   */
  private _handleContextMenu(event: Event): void {
    const customEvent = event as CustomEvent<{ node: TreeNode | null; mouseX: number; mouseY: number }>;
    const { node, mouseX, mouseY } = customEvent.detail;

    this._contextMenuNode = node;
    this._showContextMenu(mouseX, mouseY, node);
  }

  /**
   * Handle document click to close context menu
   */
  private _handleDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this._contextMenu && !this._contextMenu.contains(target)) {
      this._hideContextMenu();
    }
  }

  /**
   * Show context menu at position
   */
  private _showContextMenu(x: number, y: number, node: TreeNode | null): void {
    if (!this._contextMenu) return;

    // Build menu content
    this._buildContextMenu(node);

    // Position at cursor
    this._contextMenu.style.left = `${x}px`;
    this._contextMenu.style.top = `${y}px`;

    // Show menu
    this._contextMenu.classList.add('visible');
  }

  /**
   * Hide context menu
   */
  private _hideContextMenu(): void {
    if (!this._contextMenu) return;
    this._contextMenu.classList.remove('visible');
    this._contextMenuNode = null;
  }

  /**
   * Build context menu items for a node
   */
  private _buildContextMenu(node: TreeNode | null): void {
    if (!this._contextMenu) return;

    // If no node (right-clicked on empty space), show simplified root menu
    if (!node) {
      this._contextMenu.innerHTML = `
        <div class="context-menu-item">
          <span class="context-menu-item-icon">➕</span>
          <span class="context-menu-item-label">New</span>
          <span class="context-menu-item-arrow">▶</span>
          <div class="context-submenu">
            <div class="context-submenu-item" data-action="new-folder">
              <span class="context-submenu-item-icon">📁</span>
              <span>New Folder</span>
            </div>
            <div class="context-submenu-item" data-action="new-diagram">
              <span class="context-submenu-item-icon">📊</span>
              <span>New Diagram</span>
            </div>
          </div>
        </div>
        <div class="context-menu-item" data-action="upload">
          <span class="context-menu-item-icon">📤</span>
          <span class="context-menu-item-label">Upload Diagram...</span>
        </div>
      `;

      // Attach click handlers to direct menu items (like Upload)
      const directMenuItems = this._contextMenu.querySelectorAll('.context-menu-item[data-action]');
      directMenuItems.forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const action = (item as HTMLElement).getAttribute('data-action');
          if (action) {
            this._handleMenuAction(action, null);
          }
        });
      });

      const submenuItems = this._contextMenu.querySelectorAll('.context-submenu-item');
      submenuItems.forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const action = (item as HTMLElement).getAttribute('data-action');
          if (action) {
            this._handleMenuAction(action, null);
          }
        });
      });
      return;
    }

    // Disable options for folders
    const openDisabled = node.type === 'folder' ? 'disabled' : '';
    const pasteDisabled = !this._clipboardNode || node.type !== 'folder' ? 'disabled' : '';
    const copyIframeDisabled = node.type === 'folder' ? 'disabled' : '';
    const copyMarkupDisabled = node.type === 'folder' ? 'disabled' : '';
    const renameDisabled = node.type === 'folder' ? 'disabled' : '';
    const moveDisabled = node.type === 'folder' ? 'disabled' : '';

    this._contextMenu.innerHTML = `
      <div class="context-menu-item ${openDisabled}" data-action="open">
        <span class="context-menu-item-icon">📂</span>
        <span class="context-menu-item-label">Open</span>
      </div>
      <div class="context-menu-item ${node.type === 'folder' ? 'disabled' : ''}" data-action="download">
        <span class="context-menu-item-icon">⬇️</span>
        <span class="context-menu-item-label">Download</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item">
        <span class="context-menu-item-icon">➕</span>
        <span class="context-menu-item-label">New</span>
        <span class="context-menu-item-arrow">▶</span>
        <div class="context-submenu">
          <div class="context-submenu-item" data-action="new-folder">
            <span class="context-submenu-item-icon">📁</span>
            <span>New Folder</span>
          </div>
          <div class="context-submenu-item" data-action="new-diagram">
            <span class="context-submenu-item-icon">📊</span>
            <span>New Diagram</span>
          </div>
        </div>
      </div>
      <div class="context-menu-item ${node.type === 'folder' ? '' : 'disabled'}" data-action="upload">
        <span class="context-menu-item-icon">📤</span>
        <span class="context-menu-item-label">Upload Diagram...</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="cut">
        <span class="context-menu-item-icon">✂️</span>
        <span class="context-menu-item-label">Cut</span>
      </div>
      <div class="context-menu-item" data-action="copy">
        <span class="context-menu-item-icon">📄</span>
        <span class="context-menu-item-label">Copy</span>
      </div>
      <div class="context-menu-item ${pasteDisabled}" data-action="paste">
        <span class="context-menu-item-icon">📋</span>
        <span class="context-menu-item-label">Paste</span>
      </div>
      <div class="context-menu-item" data-action="copy-path">
        <span class="context-menu-item-icon">🔗</span>
        <span class="context-menu-item-label">Copy Path</span>
      </div>
      <div class="context-menu-item ${copyIframeDisabled}" data-action="copy-iframe">
        <span class="context-menu-item-icon">🖼️</span>
        <span class="context-menu-item-label">Copy iframe Code</span>
      </div>
      <div class="context-menu-item ${copyMarkupDisabled}" data-action="copy-markup">
        <span class="context-menu-item-icon">📝</span>
        <span class="context-menu-item-label">Copy as Markup</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item ${renameDisabled}" data-action="rename">
        <span class="context-menu-item-icon">✏️</span>
        <span class="context-menu-item-label">Rename</span>
      </div>
      <div class="context-menu-item ${moveDisabled}" data-action="move">
        <span class="context-menu-item-icon">↔️</span>
        <span class="context-menu-item-label">Move...</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="delete">
        <span class="context-menu-item-icon">🗑️</span>
        <span class="context-menu-item-label">Delete</span>
      </div>
    `;

    // Attach click handlers to menu items
    const menuItems = this._contextMenu.querySelectorAll('.context-menu-item');
    menuItems.forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        if ((item as HTMLElement).classList.contains('disabled')) {
          return;
        }
        const action = (item as HTMLElement).getAttribute('data-action');
        if (action) {
          this._handleMenuAction(action, node);
        }
      });
    });

    // Attach click handlers to submenu items
    const submenuItems = this._contextMenu.querySelectorAll('.context-submenu-item');
    submenuItems.forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        const action = (item as HTMLElement).getAttribute('data-action');
        if (action) {
          this._handleMenuAction(action, node);
        }
      });
    });
  }

  /**
   * Handle context menu action
   */
  private _handleMenuAction(action: string, node: TreeNode | null): void {
    this._hideContextMenu();

    switch (action) {
      case 'open':
        if (node && node.type === 'file') {
          this._loadDiagram(node);
        }
        break;
      case 'download':
        if (node && node.type === 'file') {
          this._downloadFile(node);
        }
        break;
      case 'new-folder':
        this._createNewFolder(node);
        break;
      case 'new-diagram':
        this._createNewDiagram(node);
        break;
      case 'upload':
        this._uploadDiagrams(node);
        break;
      case 'cut':
        if (node) this._cutNode(node);
        break;
      case 'copy':
        if (node) this._copyNode(node);
        break;
      case 'paste':
        if (node) this._pasteNode(node);
        break;
      case 'copy-path':
        if (node) this._copyPathToClipboard(node);
        break;
      case 'copy-iframe':
        if (node) this._copyIframeCode(node);
        break;
      case 'copy-markup':
        if (node) this._copyMarkup(node);
        break;
      case 'rename':
        if (node) this._renameNode(node);
        break;
      case 'move':
        if (node) this._moveNode(node);
        break;
      case 'delete':
        if (node) this._deleteNode(node);
        break;
    }
  }

  /**
   * Show toast notification
   */
  private _showToast(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    const toastManager = (window as any).QdsToastManager;
    if (toastManager && typeof toastManager[type] === 'function') {
      toastManager[type](message, { duration: 3000 });
    } else {
      console.log(`[Toast ${type}]:`, message);
    }
  }

  /**
   * Download a file
   */
  private async _downloadFile(node: TreeNode): Promise<void> {
    if (node.type !== 'file' || !this._provider) return;

    try {
      // Get file content from provider
      const content = await this._provider.getFileContent(node.id);

      // Create blob and download link
      const blob = content instanceof Blob ? content : new Blob([content], {
        type: node.metadata?.contentType || 'application/octet-stream'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = node.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this._showToast(`Downloaded "${node.name}"`, 'success');
    } catch (error) {
      this._showToast(`Failed to download: ${(error as Error).message}`, 'error');
    }
  }

  /**
   * Copy node path to clipboard
   */
  private async _copyPathToClipboard(node: TreeNode): Promise<void> {
    try {
      await navigator.clipboard.writeText(node.path);
      this._showToast('Path copied to clipboard', 'success');
    } catch (error) {
      this._showToast('Failed to copy path', 'error');
    }
  }

  /**
   * Copy iframe embed code to clipboard
   */
  private async _copyIframeCode(node: TreeNode): Promise<void> {
    if (node.type !== 'file' || !this._provider) return;

    try {
      const provider = this._provider as any;
      const storageAccount = provider.storageAccount;
      const container = provider.container;
      const pathParts = node.path.split('/');
      const fileName = pathParts.pop() || '';
      const directoryPath = pathParts.join('/');

      const iframeCode = `<iframe
  src="https://cdn.q360.ai/assets/components/diagram-host/viewer.html?storage-account=${encodeURIComponent(storageAccount)}&container=${encodeURIComponent(container)}&path=${encodeURIComponent(directoryPath)}&drawing-name=${encodeURIComponent(fileName)}"
  width="800"
  height="600"
  style="border: 1px solid var(--theme-border-primary); border-radius: 4px;"
  allowfullscreen>
</iframe>`;

      await navigator.clipboard.writeText(iframeCode);
      this._showToast('iframe code copied to clipboard', 'success');
    } catch (error) {
      this._showToast('Failed to copy iframe code', 'error');
    }
  }

  /**
   * Copy qds-diagram-host markup to clipboard
   */
  private async _copyMarkup(node: TreeNode): Promise<void> {
    if (node.type !== 'file' || !this._provider) return;

    try {
      const provider = this._provider as any;
      const storageAccount = provider.storageAccount;
      const container = provider.container;
      const pathParts = node.path.split('/');
      const fileName = pathParts.pop() || '';
      const directoryPath = pathParts.join('/');

      const markup = `<qds-diagram-host
  storage-account="${storageAccount}"
  container="${container}"
  path="${directoryPath}"
  drawing-name="${fileName}"
  width="100%"
  height="600px">
</qds-diagram-host>`;

      await navigator.clipboard.writeText(markup);
      this._showToast('Markup copied to clipboard', 'success');
    } catch (error) {
      this._showToast('Failed to copy markup', 'error');
    }
  }

  /**
   * Cut node (mark for moving)
   */
  private _cutNode(node: TreeNode): void {
    this._clipboardNode = node;
    this._clipboardOperation = 'cut';
    this._showToast(`"${node.name}" marked for moving`, 'info');
  }

  /**
   * Copy node (mark for copying)
   */
  private _copyNode(node: TreeNode): void {
    this._clipboardNode = node;
    this._clipboardOperation = 'copy';
    this._showToast(`"${node.name}" copied`, 'info');
  }

  /**
   * Paste node into target folder
   */
  private async _pasteNode(targetFolder: TreeNode): Promise<void> {
    if (!this._clipboardNode || !this._clipboardOperation || !this._provider) return;
    if (targetFolder.type !== 'folder') {
      this._showToast('Can only paste into folders', 'error');
      return;
    }

    try {
      const sourceNode = this._clipboardNode;
      const operation = this._clipboardOperation;

      if (operation === 'cut') {
        await this._provider.move(sourceNode.id, targetFolder.id);
        this._showToast(`"${sourceNode.name}" moved to "${targetFolder.name}"`, 'success');
      } else if (operation === 'copy') {
        const isSameFolder = sourceNode.parentId === targetFolder.id;
        const newName = isSameFolder ? this._generateCopyName(sourceNode.name) : sourceNode.name;

        if (sourceNode.type === 'folder') {
          await this._provider.createFolder({
            parentId: targetFolder.id,
            name: newName,
            type: 'folder'
          });
          this._showToast(`"${sourceNode.name}" copied to "${targetFolder.name}" (empty folder)`, 'info');
        } else {
          const content = await this._provider.getFileContent(sourceNode.id);
          await this._provider.createFile({
            parentId: targetFolder.id,
            name: newName,
            type: 'file',
            content: content,
            contentType: sourceNode.metadata?.contentType
          });
          this._showToast(`"${sourceNode.name}" copied to "${targetFolder.name}"`, 'success');
        }
      }

      this._clipboardNode = null;
      this._clipboardOperation = null;
    } catch (error) {
      this._showToast(`Failed to paste: ${(error as Error).message}`, 'error');
    }
  }

  /**
   * Create a new folder
   */
  private async _createNewFolder(node: TreeNode | null): Promise<void> {
    if (!this._provider) return;

    try {
      let parentId: string | null;
      if (!node) {
        parentId = null;
      } else if (node.type === 'folder') {
        parentId = node.id;
      } else {
        parentId = node.parentId || null;
      }

      const folderName = prompt('Enter folder name:');
      if (!folderName || folderName.trim() === '') return;

      const validation = this._provider.validateFileName(folderName.trim(), 'folder');
      if (!validation.isValid) {
        this._showToast(validation.error || 'Invalid folder name', 'error');
        return;
      }

      await this._provider.createFolder({
        parentId: parentId,
        name: folderName.trim(),
        type: 'folder'
      });

      this._showToast(`Folder "${folderName}" created`, 'success');
    } catch (error) {
      this._showToast(`Failed to create folder: ${(error as Error).message}`, 'error');
    }
  }

  /**
   * Create a new diagram file
   */
  private async _createNewDiagram(node: TreeNode | null): Promise<void> {
    if (!this._provider) return;

    try {
      let parentId: string | null;
      if (!node) {
        parentId = null;
      } else if (node.type === 'folder') {
        parentId = node.id;
      } else {
        parentId = node.parentId || null;
      }

      const diagramName = prompt('Enter diagram name (without extension):');
      if (!diagramName || diagramName.trim() === '') return;

      const fullName = `${diagramName.trim()}.drawio.svg`;
      const validation = this._provider.validateFileName(fullName, 'file');
      if (!validation.isValid) {
        this._showToast(validation.error || 'Invalid file name', 'error');
        return;
      }

      const emptyDiagramContent = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="1px" height="1px" viewBox="-0.5 -0.5 1 1" content="&lt;mxfile&gt;&lt;diagram name=&quot;Page-1&quot;&gt;&lt;mxGraphModel dx=&quot;800&quot; dy=&quot;800&quot; grid=&quot;1&quot; gridSize=&quot;10&quot; guides=&quot;1&quot; tooltips=&quot;1&quot; connect=&quot;1&quot; arrows=&quot;1&quot; fold=&quot;1&quot; page=&quot;1&quot; pageScale=&quot;1&quot; pageWidth=&quot;850&quot; pageHeight=&quot;1100&quot; math=&quot;0&quot; shadow=&quot;0&quot;&gt;&lt;root&gt;&lt;mxCell id=&quot;0&quot;/&gt;&lt;mxCell id=&quot;1&quot; parent=&quot;0&quot;/&gt;&lt;/root&gt;&lt;/mxGraphModel&gt;&lt;/diagram&gt;&lt;/mxfile&gt;"></svg>`;
      const blob = new Blob([emptyDiagramContent], { type: 'image/svg+xml' });

      await this._provider.createFile({
        parentId: parentId,
        name: fullName,
        type: 'file',
        content: blob,
        contentType: 'image/svg+xml'
      });

      this._showToast(`Diagram "${fullName}" created`, 'success');
    } catch (error) {
      this._showToast(`Failed to create diagram: ${(error as Error).message}`, 'error');
    }
  }

  /**
   * Upload diagram files from the desktop
   */
  private async _uploadDiagrams(node: TreeNode | null): Promise<void> {
    if (!this._provider) return;

    // Determine target folder
    let parentId: string | null;
    if (!node) {
      parentId = null;
    } else if (node.type === 'folder') {
      parentId = node.id;
    } else {
      parentId = node.parentId || null;
    }

    // Create a hidden file input
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.accept = '.drawio,.drawio.svg,.drawio.png,.drawio.xml,.xml,.svg';
    fileInput.style.display = 'none';

    // Handle file selection
    fileInput.addEventListener('change', async () => {
      const files = fileInput.files;
      if (!files || files.length === 0) {
        document.body.removeChild(fileInput);
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const file of Array.from(files)) {
        try {
          // Validate file extension
          const validation = this._provider!.validateFileName(file.name, 'file');
          if (!validation.isValid) {
            this._showToast(`Skipped "${file.name}": ${validation.error}`, 'warning');
            failCount++;
            continue;
          }

          // Check if it's a valid diagram file
          if (!this._isDrawioFile(file.name)) {
            this._showToast(`Skipped "${file.name}": Not a diagram file`, 'warning');
            failCount++;
            continue;
          }

          // Upload the file
          await this._provider!.createFile({
            parentId: parentId,
            name: file.name,
            type: 'file',
            content: file,
            contentType: file.type || this._getContentTypeForFile(file.name)
          });

          successCount++;
        } catch (error) {
          const errorMessage = (error as Error).message;
          if (errorMessage.includes('already exists') || errorMessage.includes('ALREADY_EXISTS')) {
            this._showToast(`Skipped "${file.name}": File already exists`, 'warning');
          } else {
            this._showToast(`Failed to upload "${file.name}": ${errorMessage}`, 'error');
          }
          failCount++;
        }
      }

      // Show summary toast
      if (successCount > 0 && failCount === 0) {
        this._showToast(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''} successfully`, 'success');
      } else if (successCount > 0 && failCount > 0) {
        this._showToast(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}, ${failCount} failed`, 'warning');
      }

      // Clean up
      document.body.removeChild(fileInput);
    });

    // Add to DOM and trigger click
    document.body.appendChild(fileInput);
    fileInput.click();
  }

  /**
   * Get content type for a file based on extension
   */
  private _getContentTypeForFile(filename: string): string {
    const lowerName = filename.toLowerCase();
    if (lowerName.endsWith('.drawio.svg') || lowerName.endsWith('.svg')) {
      return 'image/svg+xml';
    }
    if (lowerName.endsWith('.drawio.png') || lowerName.endsWith('.png')) {
      return 'image/png';
    }
    if (lowerName.endsWith('.drawio.xml') || lowerName.endsWith('.xml') || lowerName.endsWith('.drawio')) {
      return 'application/xml';
    }
    return 'application/octet-stream';
  }

  /**
   * Rename a node
   */
  private async _renameNode(node: TreeNode): Promise<void> {
    if (!this._provider) return;

    try {
      const newName = prompt('Enter new name:', node.name);
      if (!newName || newName.trim() === '' || newName.trim() === node.name) return;

      if (newName.includes('/') || newName.includes('\\')) {
        this._showToast('Name cannot contain path separators (/ or \\). Use "Move" to change the file location.', 'error');
        return;
      }

      const validation = this._provider.validateFileName(newName.trim(), node.type);
      if (!validation.isValid) {
        this._showToast(validation.error || 'Invalid name', 'error');
        return;
      }

      await this._provider.rename(node.id, newName.trim());
      this._showToast(`Renamed to "${newName}"`, 'success');
    } catch (error) {
      this._showToast(`Failed to rename: ${(error as Error).message}`, 'error');
    }
  }

  /**
   * Delete a node with confirmation
   */
  private async _deleteNode(node: TreeNode): Promise<void> {
    if (!this._provider) return;

    try {
      const message = node.type === 'folder'
        ? `Delete folder "${node.name}" and all its contents?`
        : `Delete file "${node.name}"?`;

      const confirmed = confirm(message);
      if (!confirmed) return;

      await this._provider.delete(node.id);
      this._showToast(`"${node.name}" deleted`, 'success');
    } catch (error) {
      this._showToast(`Failed to delete: ${(error as Error).message}`, 'error');
    }
  }

  /**
   * Move a node to a different location (rename the blob path)
   */
  private async _moveNode(node: TreeNode): Promise<void> {
    if (!this._provider) return;

    try {
      const newPath = prompt(
        `Enter new path for "${node.name}":\n\nCurrent path: ${node.path}\n\nExamples:\n- "Back Office/system.drawio.svg"\n- "engineering/diagrams/network.drawio.svg"\n- "myfile.drawio.svg" (root level)`,
        node.path
      );

      if (!newPath || newPath.trim() === '' || newPath.trim() === node.path) return;

      const trimmedPath = newPath.trim();
      const pathParts = trimmedPath.split('/');
      const newName = pathParts.pop() || '';
      const newParentPath = pathParts.join('/');
      const newParentId = newParentPath || null;

      await this._provider.move(node.id, newParentId, newName);
      this._showToast(`Moved to "${trimmedPath}"`, 'success');
    } catch (error) {
      this._showToast(`Failed to move: ${(error as Error).message}`, 'error');
    }
  }

  /**
   * Generate a copy name (e.g., "file.txt" -> "file (copy).txt")
   */
  private _generateCopyName(originalName: string): string {
    const compoundExtensions = ['.drawio.svg', '.drawio.png', '.drawio.xml'];

    for (const compoundExt of compoundExtensions) {
      if (originalName.toLowerCase().endsWith(compoundExt)) {
        const nameWithoutExt = originalName.substring(0, originalName.length - compoundExt.length);
        return `${nameWithoutExt} (copy)${compoundExt}`;
      }
    }

    const lastDot = originalName.lastIndexOf('.');
    if (lastDot === -1) {
      return `${originalName} (copy)`;
    } else {
      const nameWithoutExt = originalName.substring(0, lastDot);
      const ext = originalName.substring(lastDot);
      return `${nameWithoutExt} (copy)${ext}`;
    }
  }
}

// Register the component
if (!customElements.get('qds-diagram-manager')) {
  customElements.define('qds-diagram-manager', QdsDiagramManager);
}

export { QdsDiagramManager };
